// src/context/AppContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { loadFromLocalStorage, saveToLocalStorage } from '../services/dataService';

const AppContext = createContext();

const initialState = {
  // Données principales
  displays: [],
  templates: [],
  schedules: [],
  mediaFiles: [],
  
  // Sélections courantes
  selectedDisplay: null,
  selectedTemplate: null,
  
  // États de l'interface
  isLoading: false,
  error: null,
  notifications: [],
  
  // Paramètres
  settings: {
    siteName: 'La Grange des Petits',
    timezone: 'Europe/Paris',
    language: 'fr-FR',
    autoSave: true,
    refreshInterval: 30
  }
};

function appReducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
      
    case 'SET_ERROR':
      return { ...state, error: action.payload };
      
    case 'CLEAR_ERROR':
      return { ...state, error: null };
      
    case 'SET_DISPLAYS':
      return { ...state, displays: action.payload };
      
    case 'SET_TEMPLATES':
      return { ...state, templates: action.payload };
      
    case 'SET_SCHEDULES':
      return { ...state, schedules: action.payload };
      
    case 'SET_MEDIA_FILES':
      return { ...state, mediaFiles: action.payload };
      
    case 'SET_SELECTED_DISPLAY':
      return { ...state, selectedDisplay: action.payload };
      
    case 'SET_SELECTED_TEMPLATE':
      return { ...state, selectedTemplate: action.payload };
      
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [...state.notifications, { 
          id: Date.now(), 
          ...action.payload 
        }]
      };
      
    case 'REMOVE_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload)
      };
      
    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: { ...state.settings, ...action.payload }
      };
      
    case 'ADD_TEMPLATE':
      return {
        ...state,
        templates: [...state.templates, action.payload]
      };
      
    case 'UPDATE_TEMPLATE':
      return {
        ...state,
        templates: state.templates.map(template =>
          template.id === action.payload.id ? action.payload : template
        )
      };
      
    case 'DELETE_TEMPLATE':
      return {
        ...state,
        templates: state.templates.filter(template => template.id !== action.payload)
      };
      
    case 'ADD_DISPLAY':
      return {
        ...state,
        displays: [...state.displays, action.payload]
      };
      
    case 'UPDATE_DISPLAY':
      return {
        ...state,
        displays: state.displays.map(display =>
          display.id === action.payload.id ? action.payload : display
        )
      };
      
    case 'DELETE_DISPLAY':
      return {
        ...state,
        displays: state.displays.filter(display => display.id !== action.payload)
      };
      
    default:
      return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Charger les données au démarrage
  useEffect(() => {
    loadInitialData();
  }, []);

  // Sauvegarder automatiquement les modifications
  useEffect(() => {
    if (state.settings.autoSave) {
      const saveData = async () => {
        try {
          await saveToLocalStorage('displays', state.displays);
          await saveToLocalStorage('templates', state.templates);
          await saveToLocalStorage('schedules', state.schedules);
          await saveToLocalStorage('settings', state.settings);
        } catch (error) {
          console.error('Erreur de sauvegarde automatique:', error);
        }
      };

      const timeoutId = setTimeout(saveData, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [state.displays, state.templates, state.schedules, state.settings]);

  const loadInitialData = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      // Charger les données avec des valeurs par défaut
      const [displays, templates, schedules, settings] = await Promise.all([
        loadFromLocalStorage('displays'),
        loadFromLocalStorage('templates'),
        loadFromLocalStorage('schedules'),
        loadFromLocalStorage('settings')
      ]);

      dispatch({ type: 'SET_DISPLAYS', payload: displays });
      dispatch({ type: 'SET_TEMPLATES', payload: templates });
      dispatch({ type: 'SET_SCHEDULES', payload: schedules });
      dispatch({ type: 'UPDATE_SETTINGS', payload: settings });

      // Sélectionner le premier écran par défaut
      if (displays.length > 0) {
        dispatch({ type: 'SET_SELECTED_DISPLAY', payload: displays[0] });
      }

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Erreur lors du chargement des données' });
      console.error('Erreur de chargement:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Actions pour les notifications
  const addNotification = (notification) => {
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    
    // Auto-suppression après 5 secondes
    if (notification.autoHide !== false) {
      setTimeout(() => {
        dispatch({ type: 'REMOVE_NOTIFICATION', payload: notification.id });
      }, 5000);
    }
  };

  const removeNotification = (id) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: id });
  };

  // Actions pour les templates
  const addTemplate = async (template) => {
    try {
      const newTemplate = {
        ...template,
        id: `template-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      dispatch({ type: 'ADD_TEMPLATE', payload: newTemplate });
      await saveToLocalStorage('templates', [...state.templates, newTemplate]);
      
      addNotification({
        type: 'success',
        title: 'Template créé',
        message: `Le template "${newTemplate.name}" a été créé avec succès`
      });
      
      return newTemplate;
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Erreur lors de la création du template'
      });
      throw error;
    }
  };

  const updateTemplate = async (template) => {
    try {
      const updatedTemplate = {
        ...template,
        updatedAt: new Date().toISOString()
      };
      
      dispatch({ type: 'UPDATE_TEMPLATE', payload: updatedTemplate });
      
      const updatedTemplates = state.templates.map(t =>
        t.id === template.id ? updatedTemplate : t
      );
      await saveToLocalStorage('templates', updatedTemplates);
      
      return updatedTemplate;
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Erreur lors de la mise à jour du template'
      });
      throw error;
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      dispatch({ type: 'DELETE_TEMPLATE', payload: templateId });
      
      const updatedTemplates = state.templates.filter(t => t.id !== templateId);
      await saveToLocalStorage('templates', updatedTemplates);
      
      addNotification({
        type: 'success',
        title: 'Template supprimé',
        message: 'Le template a été supprimé avec succès'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Erreur lors de la suppression du template'
      });
      throw error;
    }
  };

  // Actions pour les écrans
  const addDisplay = async (display) => {
    try {
      const newDisplay = {
        ...display,
        id: `display-${Date.now()}`,
        createdAt: new Date().toISOString()
      };
      
      dispatch({ type: 'ADD_DISPLAY', payload: newDisplay });
      await saveToLocalStorage('displays', [...state.displays, newDisplay]);
      
      addNotification({
        type: 'success',
        title: 'Écran ajouté',
        message: `L'écran "${newDisplay.name}" a été ajouté avec succès`
      });
      
      return newDisplay;
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Erreur lors de l\'ajout de l\'écran'
      });
      throw error;
    }
  };

  const updateDisplay = async (display) => {
    try {
      dispatch({ type: 'UPDATE_DISPLAY', payload: display });
      
      const updatedDisplays = state.displays.map(d =>
        d.id === display.id ? display : d
      );
      await saveToLocalStorage('displays', updatedDisplays);
      
      return display;
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Erreur lors de la mise à jour de l\'écran'
      });
      throw error;
    }
  };

  const deleteDisplay = async (displayId) => {
    try {
      dispatch({ type: 'DELETE_DISPLAY', payload: displayId });
      
      const updatedDisplays = state.displays.filter(d => d.id !== displayId);
      await saveToLocalStorage('displays', updatedDisplays);
      
      addNotification({
        type: 'success',
        title: 'Écran supprimé',
        message: 'L\'écran a été supprimé avec succès'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Erreur lors de la suppression de l\'écran'
      });
      throw error;
    }
  };

  // Actions pour les paramètres
  const updateSettings = async (newSettings) => {
    try {
      dispatch({ type: 'UPDATE_SETTINGS', payload: newSettings });
      await saveToLocalStorage('settings', { ...state.settings, ...newSettings });
      
      addNotification({
        type: 'success',
        title: 'Paramètres mis à jour',
        message: 'Les paramètres ont été sauvegardés'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Erreur',
        message: 'Erreur lors de la sauvegarde des paramètres'
      });
      throw error;
    }
  };

  // Valeur du contexte
  const contextValue = {
    // État
    state,
    dispatch,
    
    // Actions pour les notifications
    addNotification,
    removeNotification,
    
    // Actions pour les templates
    addTemplate,
    updateTemplate,
    deleteTemplate,
    
    // Actions pour les écrans
    addDisplay,
    updateDisplay,
    deleteDisplay,
    
    // Actions pour les paramètres
    updateSettings,
    
    // Actions générales
    clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
    setSelectedDisplay: (display) => dispatch({ type: 'SET_SELECTED_DISPLAY', payload: display }),
    setSelectedTemplate: (template) => dispatch({ type: 'SET_SELECTED_TEMPLATE', payload: template }),
    
    // Utilitaires
    reloadData: loadInitialData
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;